import 'react-toastify/dist/ReactToastify.css';

import { useEffect, useState } from 'react';

import { RegistrationProvider } from './context/RegistrationContext';
import { NotificationProvider } from './context/NotificationContext';
import { RouterProvider } from './context/RouterProvider';

import config from './config';

import ErrorBoundary from './components/ErrorBoundary';
import { useDispatch } from 'react-redux';
import { setAuthData } from './store/authSlice';
import { axiosPrivate, axiosPublic } from './axios';
import { apiUser } from './api/apiUser.api';
import BannedAccountScreen from './components/BannedAccountScreen';

import './index.css';

import { toast, ToastContainer } from 'react-toastify';
import { ChatProvider } from './context/ChatProvider';
import { SenderProvider } from './context/SenderProvider';
import { ProviderTerms } from './components/terms/ProviderTerms';
import DotSpinner from './components/shared/DotSpinner';
import { useLocation, useNavigate } from 'react-router-dom';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [isBannedUser, setIsBannedUser] = useState(false);

  useEffect(() => {
    runTelegramAuth();
  }, [dispatch]);

  const runTelegramAuth = async () => {
    const tg = window.Telegram?.WebApp;

    if (tg) {
      try {
        tg.requestFullscreen && tg.requestFullscreen();
        if (tg.requestFullscreen) {
          tg.requestFullscreen();
        } else {
          tg.expand && tg.expand();
        }
        tg.setHeaderColor && tg.setHeaderColor('#010d0d');
        tg.setBackgroundColor && tg.setBackgroundColor('#010d0d');
        tg.disableVerticalSwipes && tg.disableVerticalSwipes();
        tg.MainButton && tg.MainButton.hide();
        tg.ready && tg.ready();
      } catch (error) {
        console.error('❌ Ошибка при инициализации Telegram WebApp:', error);
      }
    }

    const initData = tg?.initData;
    // tg?.initData ||
    // 'query_id=AAEZ0GYpAAAAABnQZikRKFvO&user=%7B%22id%22%3A694603801%2C%22first_name%22%3A%22Berlin%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22berlinit%22%2C%22language_code%22%3A%22ru%22%2C%22is_premium%22%3Atrue%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2FXIB8q_IQ9UeqJdisXiVUMe6aGepyrrERplMHSyO68DM.svg%22%7D&auth_date=1774682840&signature=QDZb1048niZEF8BINZxhfDZSRevcrRHcLwCbySqkyY6OH3LRcgXnQX_DIANYxbl-MwMbuHSLEZY81B3H8_heAQ&hash=a81e67aa4942236b3c6f4c7b7aec1cabc25813070c1f52b362cb81479c275cd5';

    try {
      const data = await axiosPublic
        .post('/telegram/auth', {
          initData
        })
        .then((res) => res.data.data);

      const nideRegistration = data?.needsRegistration;

      if (data.token && data.user) {
        dispatch(
          setAuthData({
            auth_token: data.token,
            userId: data.user.id,
            telegramId: data.user.telegramId
          })
        );

        setIsBannedUser(data.user.banStatus.ban);
      }
      if (nideRegistration) {
        navigate(config.ROUTES.HOME, {
          state: { needsRegistration: true, steps: data.user.profileCompleteness.steps }
        });
      } else {
        if ([config.ROUTES.HOME, config.ROUTES.MENU].includes(location.pathname)) navigate(config.ROUTES.MEET);
      }
    } catch (err) {
      console.error('❌ Ошибка при Telegram auth:', err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const updateActivity = () => {
      apiUser.fetchActivity(axiosPrivate).catch(() => {});
    };

    updateActivity();

    const interval = setInterval(updateActivity, 30_000);

    return () => clearInterval(interval);
  }, [axiosPrivate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh] [background:linear-gradient(180deg,rgb(1,13,13)_0%,rgb(3.01,42.5,42.5)_100%)] bg-[100%_100%] bg-repeat-y bg-cover">
        <DotSpinner />
      </div>
    );
  }

  if (isBannedUser) {
    return <BannedAccountScreen />;
  }

  return (
    <ErrorBoundary>
      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar
        closeButton={false}
        closeOnClick
        draggable={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        toastClassName={({ type }) =>
          [
            'flex item-start rounded-[8px] px-[10px] py-[9px] text-[14px] fonts-medium text-white border !w-[95vw]',
            type === 'success' && 'border-[#ADFFAD]',
            type === 'error' && 'border-[#FF4343]',
            type === 'warning' && 'border-[#FFB52C]',
            type === 'info' && 'border-[#37B9FF]',
            'bg-[#1B3C3C]'
          ]
            .filter(Boolean)
            .join(' ')
        }
        bodyClassName="p-0 m-0"
        icon={({ type }) => {
          switch (type) {
            case 'success':
              return <img src="/icons/toast-success.svg" alt="" className="size-5" />;
            case 'error':
              return <img src="/icons/toast-error.svg" alt="" className="size-5" />;
            case 'warning':
              return <img src="/icons/toast-warning.svg" alt="" className="size-5" />;
            case 'info':
              return <img src="/icons/toast-info.svg" alt="" className="size-5" />;
            default:
              return <img src="/icons/toast-info.svg" alt="" className="size-5" />;
          }
        }}
      />
      <RegistrationProvider>
        <ProviderTerms>
          <SenderProvider>
            <NotificationProvider>
              <ChatProvider>
                <div className="App flex flex-col justify-start items-center w-[100%] h-[100%] [background:linear-gradient(180deg,rgb(1,13,13)_0%,rgb(3.01,42.5,42.5)_100%)] bg-[100%_100%] bg-repeat-y bg-cover">
                  <RouterProvider />
                </div>
              </ChatProvider>
            </NotificationProvider>
          </SenderProvider>
        </ProviderTerms>
      </RegistrationProvider>
    </ErrorBoundary>
  );
}

export default App;
